import {usePartnerUi} from "~/contexts/PartnerUiContext";
import {PaymentDetails, PaymentType} from "@bounce/shared/consumer/declarations";
import {
  BackToDashboardButton,
  mapMethodToValue,
  PaymentLoadingButton,
  PaymentMethodType,
  PaymentTermsAndConditions,
  SelectPaymentMethod,
} from "@bounce/shared/consumer/pages/account-dashboard";
import {
  getPaymentType,
  PaymentContext,
  PaymentTermsAndConditionsV2,
  TermsAndConditionsApproval,
  TermsAndConditionsDialog,
  TermsType,
} from "@bounce/shared/consumer/pages/payment";
import {PaymentMethodTypes} from "@bounce/shared/enums";
import React, {useContext, useMemo, useState} from "react";
import {useGetUserQuery} from "~/api/requests";
import {Pages} from "~/bi-events/enum";
import {Container} from "@bounce/shared/ui";
import {useAmplitude} from "~/contexts";
import PaymentTabs from "~/pages/account/payment/components/payment-form/PaymentTabs";

export type IPaymentMethod = {
  accountId: string;
  lastDigitsCard?: string | undefined;
  title?: string;
  submitTitle?: string;
  currentMethodType?: PaymentMethodTypes;
  paymentHandler: (paymentType: PaymentType, paymentDetails: PaymentDetails, isChangePaymentMethod: boolean) => void;
  disable?: boolean;
  page?: Pages;
  termsAndConditionsId?: string;
  closeHandler?: () => void;
  paymentPlanId?: string;
  backToDashboardButton?: boolean;
  handleBackToDashboard?: () => void;
  inManagePaymentMethod?: boolean;
  immediatePaymentAmount: string;
};

const PaymentMethod = ({
  accountId,
  lastDigitsCard,
  title,
  paymentHandler,
  submitTitle = "Pay it now",
  disable = false,
  currentMethodType,
  page,
  closeHandler,
  termsAndConditionsId = TermsType.PAYMENT,
  backToDashboardButton = false,
  handleBackToDashboard,
  immediatePaymentAmount,
}: IPaymentMethod) => {
  const {pageClickEvent, pageViewEvent} = useAmplitude();
  const {partnerDetails} = usePartnerUi();
  const [checkedTerms, setCheckedTerms] = useState<boolean>(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodType>("current");
  const {buttonTouched, isLoading} = useContext(PaymentContext);
  const {data: user} = useGetUserQuery({});
  const achBlocked = useMemo(() => {
    return user && !user.ach_allowed && currentMethodType === "ach";
  }, [user]);

  const paymentMethodOption = useMemo(() => {
    const options = mapMethodToValue({
      currentMethodType,
      lastDigitsCard,
      showRemoveOption: false,
    });
    if (!Object.keys(options).includes("current") || achBlocked) {
      setPaymentMethod("change");
    }
    return options;
  }, [user]);

  const hasPaymentMethod = paymentMethodOption && lastDigitsCard;

  const termsAndConditionsProps = {
    termsAndConditionsId,
    TermsAndConditionsDialog,
    PaymentTermsAndConditionsV2,
    partnerDetails,
    page,
    pageClickEvent,
    pageViewEvent,
    page_T_C: Pages.T_C,
    sx: {textAlign: "start"},
  };

  return (
    <Container fullWidth sx={{alignItems: "start", gap: "20px"}}>
      <Container fullWidth sx={{alignItems: "start", gap: "15px"}}>
        <SelectPaymentMethod
          hasPaymentMethod={hasPaymentMethod}
          achBlocked={achBlocked}
          title={title}
          paymentMethod={paymentMethod}
          paymentMethodOption={paymentMethodOption}
          setPaymentMethod={setPaymentMethod}
        />
        {(!lastDigitsCard || paymentMethod === "change") && (
          <PaymentTabs
            onCardSubmit={paymentHandler}
            onAchSubmit={paymentHandler}
            submitLabelId={submitTitle}
            termsAndConditionsId={termsAndConditionsId}
            isDashboardDialog={true}
            closePaymentDialogHandler={closeHandler}
            backToDashboardButton={backToDashboardButton}
            handleBackToDashboard={handleBackToDashboard}
            accountId={accountId}
            immediatePaymentAmount={immediatePaymentAmount}
          />
        )}
      </Container>
      {lastDigitsCard && paymentMethod === "current" && (
        <Container fullWidth>
          <PaymentTermsAndConditions
            paymentMethod={paymentMethod}
            checkedTerms={checkedTerms}
            setCheckedTerms={setCheckedTerms}
            TermsAndConditionsApproval={TermsAndConditionsApproval}
            termsAndConditionsProps={termsAndConditionsProps}
          />
          <PaymentLoadingButton
            paymentMethod={paymentMethod}
            submitTitle={submitTitle}
            checkedTerms={checkedTerms}
            disable={disable}
            buttonTouched={buttonTouched}
            isLoading={isLoading}
            onPaymentSubmit={() => paymentHandler(getPaymentType(currentMethodType), {}, false)}
          />
          <BackToDashboardButton
            backToDashboardButton={backToDashboardButton}
            handleBackToDashboard={handleBackToDashboard}
          />
        </Container>
      )}
    </Container>
  );
};

export default PaymentMethod;
